import DeleteCampaignImageButton from "components/DeleteCampaignImageButton";
import UploadImagesButtons from "components/UploadImagesButton";
import React, { useCallback, useEffect, useState } from "react";
import { ImageAttachedToKind } from "types/globalTypes";
import styles from "./style.module.scss";

type Props = {
  images: { id: string; url: string }[];
  initial?: number;
  title: string;
  onClose: () => void;
  imageUpload?: {
    attachedToKind: ImageAttachedToKind;
    attachedToId: string;
    onImageUploadComplete: Function;
  };
  deleteCampaignListingImages?: {
    campaignListingUUID: string;
    campaignImages: { id: string; uuid: string }[];
  };
};

const Lightbox = ({
  images,
  initial = 0,
  title,
  onClose,
  imageUpload,
  deleteCampaignListingImages,
}: Props) => {
  const [index, setIndex] = useState(initial);

  const currentCampaignImageInfo =
    deleteCampaignListingImages?.campaignImages.find(
      (image) => images[index].id === image.id
    );

  if (document) {
    document.body.style.overflow = "hidden";
  }

  const handleClose = useCallback(() => {
    if (document) {
      document.body.style.overflow = "unset";
    }
    onClose();
  }, [onClose]);

  const decrementIndex = useCallback(() => {
    if (index - 1 >= 0) {
      setIndex(index - 1);
    } else {
      setIndex(0);
    }
  }, [setIndex, index]);

  const incrementIndex = useCallback(() => {
    if (index + 1 <= images.length - 1) {
      setIndex(index + 1);
    } else {
      setIndex(images.length - 1);
    }
  }, [setIndex, index, images.length]);

  useEffect(() => {
    const handler = (evt: KeyboardEvent) => {
      evt.stopPropagation();
      evt.stopImmediatePropagation();
      evt.preventDefault();
      const { key } = evt;
      if (key === "ArrowLeft") decrementIndex();
      if (key === "ArrowRight") incrementIndex();
      if (key === "Escape") handleClose();
    };
    window.addEventListener("keydown", handler);

    return () => {
      window.removeEventListener("keydown", handler);
    };
  }, [incrementIndex, decrementIndex, handleClose]);

  // given an empty array show nothing
  if (!images.length) return null;

  const image = images[index];
  const isDefaultImage = [
    "/nextpublic/zeroState-mobile@2x.png",
    "/nextpublic/zeroState@2x.png",
  ].includes(image.url);

  return (
    <div className={styles.container}>
      <div className={styles.inner}>
        <button
          data-qa="left-arrow"
          className={`${styles.arrowButton} ${styles.leftButton}`}
          type="button"
          onClick={decrementIndex}
          disabled={index === 0}
        >
          <span className="icon icon-chevron-left" />
        </button>

        <header className={styles.address}>
          <h1>{title}</h1>
          {!!currentCampaignImageInfo &&
            !!deleteCampaignListingImages?.campaignListingUUID && (
              <DeleteCampaignImageButton
                campaignListingUUID={
                  deleteCampaignListingImages.campaignListingUUID
                }
                imageUUID={currentCampaignImageInfo.uuid}
                beforeMutationCallback={() => {
                  decrementIndex();
                }}
              />
            )}
          <button
            type="button"
            onClick={handleClose}
            data-qa="lightBoxClose"
            className={styles.close}
          >
            <span className="icon icon-cross" />
          </button>
        </header>
        <div
          className={styles.imageContainer}
          data-qa={`lightbox-img-${image.id}`}
        >
          {isDefaultImage && (
            <div className={styles.defaultImageCopy}>
              <p>
                We&apos;re in the process of <br />
                getting photos for this space.
              </p>
            </div>
          )}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img alt={`listing ${title}`} src={image.url} />
        </div>
        <button
          data-qa="right-arrow"
          className={`${styles.arrowButton} ${styles.rightButton}`}
          type="button"
          onClick={incrementIndex}
          disabled={index === images.length - 1}
        >
          <span className="icon icon-chevron-right" />
        </button>
      </div>
      {!!imageUpload && (
        <UploadImagesButtons
          attachedToId={imageUpload.attachedToId}
          attachedToKind={imageUpload.attachedToKind}
          labelCTA="UPLOAD PHOTOS"
          onComplete={imageUpload.onImageUploadComplete}
          className={`btn-primary ${styles.uploadButton}`}
        />
      )}
    </div>
  );
};

export default Lightbox;
